import { publicEnv } from '@/lib/config/public-env';
import { hasViewAsSession } from '@/@subsidiary/view-as-store';
import { tracking } from '@/lib/features/tracking';
import { EVENTS, EventName } from '@/lib/features/tracking/events';
import { ServiceConfig, getSessionToken } from '@/lib/utils/session';
import {
    guardViewAsWrite,
    recordViewAsWrite,
    type GuardedWrite,
} from '@/lib/view-as/backstop-shared';
import { graphqlWriteOperation } from '@/lib/view-as/write-policy';
import { DocumentNode } from 'graphql';
import { GraphQLClient } from 'graphql-request';

const API_URL = publicEnv('NEXT_PUBLIC_PORTAL_API_BASE_ENDPOINT');
const GRAPHQL_URL = API_URL ? `${API_URL}/graphql` : undefined;

/**
 * View-as write backstop for GraphQL (M4S-5670 §6). A query is a read and
 * passes. A mutation is a write: it goes through the shared guard with its
 * root field names as the operation, and only the view-filter mutations are
 * allowed. A refusal rejects with ViewAsWriteRefusedError before any request.
 * A no-op unless the tab holds a View-as session.
 */
async function guardGraphqlWrite(queryString: string): Promise<GuardedWrite | null> {
    if (!hasViewAsSession()) return null;
    const operation = graphqlWriteOperation(queryString);
    if (operation == null) return null;
    return guardViewAsWrite({ client: 'graphql', method: 'POST', path: '/graphql', operation });
}

/**
 * Gets or creates a GraphQL client with the current session token
 */
async function getGraphQLClient(config: ServiceConfig) {
    const token = await getSessionToken(config);

    if (!GRAPHQL_URL) {
        throw new Error('GRAPHQL_URL is not configured');
    }

    if (!token) {
        tracking.event(EVENTS.USER.SESSION_ERROR, {
            error: 'No authentication token available',
            feature: config.featureName,
            hasToken: false,
        });
        throw new Error('No authentication token available');
    }

    // Always create a new client to ensure fresh headers
    return new GraphQLClient(GRAPHQL_URL, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Extract the operation name from a GraphQL query string
 */
function extractQueryName(queryString: string): string {
    // Match patterns like "query GetQuestions" or "mutation UpdateQuestion"
    const match = queryString.match(/(?:query|mutation)\s+(\w+)/);
    return match?.[1] || 'unknown_operation';
}

/**
 * Makes a GraphQL request with proper error handling and tracking
 * Can be used by any service in the application
 */
export async function graphqlRequest<T = unknown>({
    query,
    variables,
    config,
}: {
    query: string | DocumentNode;
    variables?: Record<string, unknown>;
    config: ServiceConfig;
}): Promise<{ data: T }> {
    const startTime = Date.now();
    const source = `${config.serviceName}.graphqlRequest`;
    const queryString = typeof query === 'string' ? query : query.loc?.source.body || '';
    const queryName = extractQueryName(queryString);

    // Outside the try: a refused View-as write is not a request error to track.
    const viewAsWrite = queryString ? await guardGraphqlWrite(queryString) : null;

    try {
        if (!queryString) {
            throw new Error('Invalid GraphQL query');
        }

        const client = await getGraphQLClient(config);
        if (!client) {
            throw new Error('Failed to initialize GraphQL client');
        }

        // Log the request. The variables stay out: this event goes to LogRocket
        // as-is, around the network sanitizer, and network capture already
        // records the request body through it (M4S-5615).
        const requestProps = {
            url: GRAPHQL_URL,
            query: queryName,
            feature: config.featureName,
            timestamp: Date.now(),
        };
        tracking.event(EVENTS.API.REQUEST, requestProps);

        const timeoutMs = process.env.NODE_ENV === 'development' ? 30_000 : 60_000;
        const data = await client.request<T>({
            document: queryString,
            variables,
            signal: AbortSignal.timeout(timeoutMs),
        });
        const duration = Date.now() - startTime;

        // graphql-request resolves only on a 2xx with no errors.
        recordViewAsWrite(viewAsWrite, 200);

        // Log success
        const successProps = {
            operation: queryName,
            duration,
            success: true,
            feature: config.featureName,
            timestamp: Date.now(),
        };
        tracking.event(EVENTS.API.SUCCESS, successProps);

        // Track performance
        tracking.metric(EVENTS.PERFORMANCE.API_LATENCY, duration, {
            operation: queryName,
            feature: config.featureName,
        });

        return { data };
    } catch (error) {
        const duration = Date.now() - startTime;
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isAuthError =
            errorMessage.toLowerCase().includes('invalid token') ||
            errorMessage.toLowerCase().includes('unauthorized');

        // Log error
        const errorProps = {
            operation: queryName,
            duration,
            success: false,
            error: errorMessage,
            feature: config.featureName,
            timestamp: Date.now(),
        };
        tracking.event(EVENTS.API.ERROR, errorProps);

        tracking.errorWithSource(error instanceof Error ? error : new Error(errorMessage), source, {
            variables,
            duration,
            queryName,
            operation: 'graphql_request',
            isAuthError,
            feature: config.featureName,
        });

        throw error;
    }
}

/**
 * Type for operation hooks
 */
type OperationHooks<TResult, TInput> = {
    /**
     * Called before the operation starts
     */
    onStart?: (input: TInput) => void;

    /**
     * Called when the operation is successful
     */
    onSuccess?: (result: TResult, input: TInput, duration: number) => void;

    /**
     * Called when the operation is empty (no data)
     */
    onEmpty?: (input: TInput, duration: number) => void;

    /**
     * Called when the operation fails
     */
    onError?: (error: Error, input: TInput, duration: number) => void;
};

/**
 * Creates a GraphQL service with tracking and error handling
 *
 * @example
 * ```typescript
 * // Create a GraphQL service for a specific feature
 * const graphqlService = createGraphQLService({
 *   serviceName: 'QuestionService',
 *   featureName: 'portal.questions'
 * });
 *
 * // Use the service in your feature implementation
 * const { data } = await graphqlService.request<YourResponseType>(QUERY, variables);
 * ```
 */
export function createGraphQLService(config: ServiceConfig) {
    const service = {
        /**
         * Makes a GraphQL request with proper error handling and tracking
         */
        request: async <T = unknown>(
            query: string | DocumentNode,
            variables?: Record<string, unknown>,
        ): Promise<{ data: T }> => {
            return graphqlRequest<T>({
                query,
                variables,
                config,
            });
        },

        /**
         * Tracks a feature-specific event with the given properties
         */
        trackEvent: (eventType: EventName, properties: Record<string, unknown>): void => {
            tracking.event(eventType, {
                ...properties,
                feature: config.featureName,
                timestamp: Date.now(),
            });
        },

        /**
         * Tracks a performance metric with the given name and value
         */
        trackMetric: (
            metricName: EventName,
            value: number,
            properties?: Record<string, unknown>,
        ): void => {
            tracking.metric(metricName, value, {
                ...(properties || {}),
                feature: config.featureName,
            });
        },

        /**
         * Tracks an error with source information
         */
        trackError: (
            error: Error | string,
            source: string,
            properties?: Record<string, unknown>,
        ): void => {
            const errorObj = typeof error === 'string' ? new Error(error) : error;
            tracking.errorWithSource(errorObj, `${config.serviceName}.${source}`, {
                ...(properties || {}),
                feature: config.featureName,
            });
        },

        /**
         * Executes an operation with automatic tracking and hooks
         *
         * @example
         * ```typescript
         * const result = await graphqlService.executeOperation({
         *   name: 'getQuestions',
         *   input: { surveyIds, viewFilterId },
         *   execute: async () => {
         *     const { data } = await graphqlService.request<YourResponseType>(QUERY, variables);
         *     return data;
         *   },
         *   events: {
         *     start: EVENTS.QUESTIONS.FETCH_START,
         *     success: EVENTS.QUESTIONS.FETCH_SUCCESS,
         *     empty: EVENTS.QUESTIONS.FETCH_EMPTY,
         *     error: EVENTS.QUESTIONS.FETCH_ERROR
         *   },
         *   isEmpty: (result) => !result?.items?.length,
         * });
         * ```
         */
        executeOperation: async <TResult, TInput extends Record<string, unknown>>({
            name,
            input,
            execute,
            events,
            isEmpty,
            hooks,
        }: {
            name: string;
            input: TInput;
            execute: () => Promise<TResult>;
            events?: {
                start?: EventName;
                success?: EventName;
                empty?: EventName;
                error?: EventName;
            };
            isEmpty?: (result: TResult) => boolean;
            hooks?: OperationHooks<TResult, TInput>;
        }): Promise<TResult> => {
            const startTime = Date.now();

            // Track start event if provided
            if (events?.start) {
                service.trackEvent(events.start, {
                    ...input,
                    timestamp: Date.now(),
                });
            }

            // Call onStart hook if provided
            if (hooks?.onStart) {
                hooks.onStart(input);
            }

            try {
                const result = await execute();
                const duration = Date.now() - startTime;

                // Check if result is empty
                const isEmptyResult = isEmpty ? isEmpty(result) : false;

                if (isEmptyResult) {
                    // Track empty event if provided
                    if (events?.empty) {
                        service.trackEvent(events.empty, {
                            ...input,
                            duration,
                            timestamp: Date.now(),
                        });
                    }

                    // Call onEmpty hook if provided
                    if (hooks?.onEmpty) {
                        hooks.onEmpty(input, duration);
                    }
                } else {
                    // Track success event if provided
                    if (events?.success) {
                        service.trackEvent(events.success, {
                            ...input,
                            duration,
                            timestamp: Date.now(),
                        });
                    }

                    // Call onSuccess hook if provided
                    if (hooks?.onSuccess) {
                        hooks.onSuccess(result, input, duration);
                    }
                }

                // Track performance
                service.trackMetric(EVENTS.PERFORMANCE.API_LATENCY, duration, {
                    operation: name,
                });

                return result;
            } catch (error) {
                const duration = Date.now() - startTime;
                const errorObj = error instanceof Error ? error : new Error(String(error));

                // Track error event if provided
                if (events?.error) {
                    service.trackEvent(events.error, {
                        ...input,
                        error: errorObj.message,
                        duration,
                        timestamp: Date.now(),
                    });
                }

                // Track error
                service.trackError(errorObj, name, {
                    ...input,
                    duration,
                });

                // Call onError hook if provided
                if (hooks?.onError) {
                    hooks.onError(errorObj, input, duration);
                }

                throw error;
            }
        },
    };

    return service;
}

/**
 * @deprecated Use createGraphQLService instead
 */
export function createGraphQLRequest(config: ServiceConfig) {
    return async <T = any, V extends object = object>(
        query: string,
        variables?: V,
        operationName?: string,
    ): Promise<T> => {
        const viewAsWrite = await guardGraphqlWrite(query);
        try {
            const client = await getGraphQLClient(config);
            const result = await client.request<T>(query, variables);
            recordViewAsWrite(viewAsWrite, 200);
            return result;
        } catch (error) {
            tracking.errorWithSource(
                error instanceof Error ? error : new Error(String(error)),
                `${config.serviceName}.graphqlRequest`,
                {
                    operation: operationName || 'unknown',
                    phase: 'request',
                    error: error instanceof Error ? error.message : String(error),
                    feature: config.featureName,
                },
            );
            throw error;
        }
    };
}
